/**
 * CDD Legal Person — Shareholder(s)/Beneficial Owner(s) 자동 입력 로직 (순수 함수).
 *
 * 데이터 소스: getActiveMembers(entityCd) (= 현재 유효 register of members, L_SHAREHOLDER_DETAIL, IS_DEL=0).
 *   각 member: sharesOrdinary/sharesPreference/sharesOthers (base class 별 주식수), numberOfShares.
 *   → base share class(Ordinary/Preference/Others) 별로 행을 분리한다.
 * 지분율: 해당 클래스 주식 ÷ 전체 발행주식(모든 member·모든 클래스 합) × 100  (사용자 확정 기준, 합계=100%).
 * 하드코딩·임의값 없음 — 클래스 라벨은 시스템 데이터 모델의 base class(Ordinary/Preference/Others).
 * ⚠ 커스텀 sub-class(L_SUB_CLASS_DETAIL)는 members API 에 없어 후속(중복집계 확인 필요).
 */
import type { ComboOpt } from './cddOptions';

export interface ShareholderRow { name: string; shareType: string; shares: number; pct: string; }

// MY base share class = 시스템이 3개 고정 컬럼으로 저장 (하드코딩이 아니라 데이터 모델).
const BASE_CLASSES: { key: string; label: string }[] = [
  { key: 'sharesOrdinary', label: 'Ordinary' },
  { key: 'sharesPreference', label: 'Preference' },
  { key: 'sharesOthers', label: 'Others' },
];

function pickName(m: any): string {
  return String(
    m?.name || m?.contactName || [m?.firstName, m?.middleName, m?.surname].filter(Boolean).join(' ') || '',
  ).trim();
}
function num(v: any): number { const n = Number(v); return Number.isFinite(n) ? n : 0; }

/** 지분율 표시: 최대 소수 2자리 + 불필요한 0 제거 + % (25.00→25%, 33.3333→33.33%, 33.3→33.3%). */
export function formatPct(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  const rounded = Math.round(n * 100) / 100;
  return `${String(rounded)}%`; // String() 이 trailing 0 제거
}

/** 활성 member 목록 → (주주 × 보유 클래스) 행 분리 + 지분율. 동일 (이름,클래스) 중복 제거. */
export function buildShareholderRows(members: any[]): ShareholderRow[] {
  const list = Array.isArray(members) ? members : [];
  // 전체 발행주식 = 모든 member 의 모든 클래스 주식 합 (분모)
  let totalIssued = 0;
  for (const m of list) for (const c of BASE_CLASSES) totalIssued += num(m[c.key]);

  const rows: ShareholderRow[] = [];
  const seen = new Set<string>();
  for (const m of list) {
    const name = pickName(m);
    if (!name) continue;
    for (const c of BASE_CLASSES) {
      const shares = num(m[c.key]);
      if (shares <= 0) continue;
      const dedup = `${name.toUpperCase()}|${c.label.toUpperCase()}`;
      if (seen.has(dedup)) continue;
      seen.add(dedup);
      const pct = totalIssued > 0 ? (shares / totalIssued) * 100 : 0;
      rows.push({ name, shareType: c.label, shares, pct: formatPct(pct) });
    }
  }
  return rows;
}

/** MY base Share Class 고정 3종 — 콤보 옵션 (데이터 보유 여부와 무관하게 항상 노출). */
export const SHARE_CLASS_OPTIONS: ComboOpt[] = BASE_CLASSES.map((c) => ({
  value: c.label,
  label: c.label,
}));

/** @deprecated 항상 SHARE_CLASS_OPTIONS 반환 — 기존 호출부 호환용. */
export function shareClassOptionsFromRows(_rows?: ShareholderRow[]): ComboOpt[] {
  return SHARE_CLASS_OPTIONS;
}

/** BO Type → CDD "Type of ownership/control" 표시 문구. */
const BO_TYPE_RELATIONSHIP: Record<number, string> = {
  1: 'Direct ownership',
  2: 'Indirect ownership',
  3: 'Direct & Indirect ownership',
  4: 'Control by other means',
};

function normalizeBoDesc(s: string): string {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s*&\s*/g, ' and ');
}

function resolveBoRelationship(b: any): string {
  const desc = String(b?.boTypeDescription || '').trim();
  if (desc) return desc;
  const id = Number(b?.boTypeId ?? b?.BOTypeId ?? 0);
  if (BO_TYPE_RELATIONSHIP[id]) return BO_TYPE_RELATIONSHIP[id];
  const legacy = String(b?.typeOfBo || '').trim().toUpperCase();
  if (legacy === 'D') return BO_TYPE_RELATIONSHIP[1];
  if (legacy === 'I') return BO_TYPE_RELATIONSHIP[2];
  if (legacy === 'DI') return BO_TYPE_RELATIONSHIP[3];
  if (legacy === 'C') return BO_TYPE_RELATIONSHIP[4];
  if (b?.isJointArrangement) return 'Joint arrangement';
  if (b?.isSeniorManagement) return 'Senior management';
  return '';
}

/** CDD "through other means" — Control by other means(type 4) 또는 그에 준하는 플래그. */
export function isBoThroughOtherMeans(b: any): boolean {
  const id = Number(b?.boTypeId ?? b?.BOTypeId ?? 0);
  if (id === 4) return true;
  const desc = normalizeBoDesc(b?.boTypeDescription || '');
  if (desc === 'control by other means' || desc.includes('control by other means')) return true;
  if (b?.isJointArrangement) return true;
  const legacy = String(b?.typeOfBo || '').trim().toUpperCase();
  return legacy === 'C';
}

/**
 * Beneficial Owners through other means 행.
 * L_BENEFICIALOWNER 활성 BO 전원 → Name + Type of ownership/control(= BO Type 라벨).
 * (주주 표와 이름이 겹쳐도 BO 등록 목적이 다르므로 포함; 이름 중복만 제거)
 */
export function buildBoRows(bos: any[], _shareholderNames?: string[]): { name: string; relationship: string }[] {
  const out: { name: string; relationship: string }[] = [];
  const seen = new Set<string>();

  for (const b of Array.isArray(bos) ? bos : []) {
    const name = String(b?.name || b?.contactName || b?.fullName || '').trim();
    if (!name) continue;
    const up = name.toUpperCase();
    if (seen.has(up)) continue;
    seen.add(up);
    out.push({ name, relationship: resolveBoRelationship(b) });
  }
  return out;
}
